import {
  CellAddress,
  CoObjectItem,
  CoObjectSummary,
  DocumentPartSource,
  TgkAmountObjectSource,
} from "../../components/interfaces/CoObject";
import {
  deleteXMLPart,
  DocPropKeys,
  getCustomXmlByContentControlId,
  retrieveDocumentProp,
  retrieveDocumentPropFromContext,
} from "./CustomXmlHelper";
import NotificationManager from "../../common/notification/NotificationManager";
import { DELETE_OBJECT_NOTIFICATION } from "../constants/notification";
import { formatMessage, formatMessageWithParams } from "./LanguageHelper";
import { CoObjectTypeEnum } from "../../api/models";
import { clearInheritedInfo } from "./InheritanceHelper";
import { isComponentInstance } from "./ComponentInstanceHelper";
import AsyncObjectUpdateManager from "../../common/update/AsyncObjectUpdateManager";
import type { ElaborationStatusItem } from "../../redux/feature/object/remote-elaborations-slice";
import { modifyCannotDelete } from "../../office/ContentControlModifiers";
import { SourceTypeEnum } from "../../api/models/source-type-enum";

export const addTitleAndTag = async (contentControlId: number, coObject: CoObjectItem) => {
  await Word.run(async (context) => {
    let contentControl = context.document.contentControls.getById(contentControlId);
    updateTitleAndTag(contentControl, coObject);
    await context.sync();
  });
};

export const editTitleAndTag = (coObject: CoObjectItem) => {
  void Word.run(async (context) => {
    let contentControl = context.document.contentControls.getById(Number(coObject.contentControlId));
    updateTitleAndTag(contentControl, coObject);
    await context.sync();

    // for legacy objects, delete the custom xml part
    await deleteXMLPart(coObject.contentControlId);
  });
};

const updateTitleAndTag = (contentControl: Word.ContentControl, coObject: CoObjectItem) => {
  const cleanedObject = clearInheritedInfo(coObject);
  contentControl.title = coObject.info.objectName;
  contentControl.tag = JSON.stringify(cleanedObject);
};

export const deleteContentControlById = async (
  contentControlId: number,
  notificationOnError: boolean = true
): Promise<any> => {
  try {
    return await Word.run(async (context) => {
      let contentControl = context.document.contentControls.getById(contentControlId);
      // for legacy objects, delete the custom xml part
      await deleteXMLPart(contentControlId);

      await modifyCannotDelete(context, contentControl, false);
      await context.sync();
      contentControl.delete(false);
      await context.sync();
    });
  } catch (e) {
    console.error(e);
    if (notificationOnError) {
      NotificationManager.addNotification({
        key: DELETE_OBJECT_NOTIFICATION,
        message: formatMessageWithParams("cof.elaboration.deleteobject.message.notdelete", {
          coObjectName: "coObject",
          exception: e.message,
        }),
        title: formatMessage("cof.createnewobject.message.deletefailed.title"),
        type: "error",
        closable: true,
        removeOnClose: true,
        isTemporary: false,
      });
    }
    throw e instanceof Error ? e : new Error(String(e));
  }
};

export const getDocumentCoObjects = async (): Promise<CoObjectItem[]> => {
  return Word.run(async (context) => {
    const documentId = await retrieveDocumentPropFromContext(context, DocPropKeys.DocumentId);
    const contentControls = context.document.contentControls;
    contentControls.load("id, tag");
    await context.sync();

    console.log(`${contentControls?.items?.length} content controls has been found in the document`);

    let parsedCoObjects: CoObjectItem[] = [];
    if (contentControls?.items)
      for (const cc of contentControls.items) {
        let parsedCoObject: CoObjectItem = await getCoObjectFromTagOrCustomXmlByContentControl(cc);

        if (parsedCoObject?.documentId === documentId && parsedCoObject?.contentControlId === cc.id) {
          parsedCoObjects.push(parsedCoObject);
        }
      }

    console.log(`${parsedCoObjects.length} coObjects has been retrieved from the document`);
    return parsedCoObjects;
  });
};

export const getAllCoObjects = async (objectTypes?: string[]): Promise<CoObjectItem[]> => {
  return Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load("id, tag");
    await context.sync();

    console.log(`${contentControls?.items?.length} content controls has been found in the document`);

    const allCoObjects: CoObjectItem[] = [];
    if (contentControls?.items)
      for (const cc of contentControls.items) {
        let parsedCoObject: CoObjectItem = await getCoObjectFromTagByContentControl(cc);
        if (
          parsedCoObject?.info &&
          parsedCoObject.info.objectType !== "DOCUMENT_PART_INCLUDE" &&
          (!objectTypes || objectTypes.length === 0 || objectTypes.includes(parsedCoObject.info.objectType))
        ) {
          parsedCoObject.contentControlId = cc.id;
          allCoObjects.push(parsedCoObject);
        }
      }

    console.log(`${allCoObjects.length} coObjects has been retrieved from the document`);
    return allCoObjects;
  });
};

/**
 * Resolves the CoObject stored in the tag/customXml of the content control identified by `contentControlId`.
 *
 * The `contentControlId` embedded in the tag/customXml is always validated against the live Word content
 * control id (this protects against foreign/pasted content). Validating the `documentId` against the root
 * `Document-ID` is controlled by `checkDocumentId`:
 * - `true` (strict): rejects a stale `documentId`, e.g. content physically inlined from a
 *   DOCUMENT_PART_INCLUDE wrapper that kept its old documentId after an id-regenerating backend operation
 *   (export/import, duplicate).
 * - `false` (relaxed): ignores the `documentId` entirely.
 */
const resolveCoObjectByContentControlId = async (
  contentControlId: number,
  { checkDocumentId }: { checkDocumentId: boolean }
): Promise<CoObjectItem> => {
  try {
    return await Word.run(async (context) => {
      let contentControl = context.document.contentControls.getById(contentControlId);

      contentControl.load("id, tag");
      await context.sync();
      const coObject = await getCoObjectFromTagOrCustomXmlByContentControl(contentControl);
      if (!coObject) {
        console.warn(
          `No Tagetik metadata found (neither tag nor customXml) for contentControl with id ${contentControlId}, it will be dismissed as a non Tagetik object.`
        );
        return null;
      }
      if (Number(coObject.contentControlId) !== contentControlId) {
        console.warn(
          "Warning: the object being edited has contentControlId mismatch between document and object, it will be dismissed as a non Tagetik object",
          coObject
        );
        return null;
      }
      if (checkDocumentId) {
        const documentId = await retrieveDocumentProp(DocPropKeys.DocumentId);
        if (coObject?.documentId !== documentId) {
          console.warn(
            "Warning: the object being edited has documentId mismatch between document and object, it will be dismissed as a non Tagetik object",
            coObject
          );
          return null;
        }
      }
      return coObject;
    });
  } catch (e) {
    console.warn(`ContentControl with id ${contentControlId} has not been found in the document.`, e);
    return null;
  }
};

export const getCoObjectFromTagOrCustomXmlByContentControlId = async (
  contentControlId: number
): Promise<CoObjectItem> => resolveCoObjectByContentControlId(contentControlId, { checkDocumentId: true });

/**
 * Same as {@link getCoObjectFromTagOrCustomXmlByContentControlId} but ignores the `documentId` equality
 * check against the root `Document-ID`. Content physically inlined inside a DOCUMENT_PART_INCLUDE wrapper
 * keeps its original documentId after an id-regenerating backend operation (export/import, duplicate), so
 * the strict resolver would otherwise reject it even though it is live, first-party content.
 *
 * The `contentControlId` match check is still enforced, so foreign/pasted content is still rejected. Callers
 * of this relaxed resolver are responsible for any additional ownership checks (e.g. verifying the content
 * control descends from a root-matching DOCUMENT_PART_INCLUDE wrapper) before trusting the result.
 */
export const getCoObjectFromTagOrCustomXmlByContentControlIdIgnoringDocumentId = async (
  contentControlId: number
): Promise<CoObjectItem> => resolveCoObjectByContentControlId(contentControlId, { checkDocumentId: false });

export const getCoObjectFromTagOrCustomXmlByContentControl = async (contentControl: Word.ContentControl) => {
  let parsedCoObject = undefined;
  if (contentControl?.tag) {
    try {
      parsedCoObject = JSON.parse(contentControl.tag);
    } catch (err) {
      console.warn(
        `Error For contentControl with ID: ${contentControl.id}, parsing TAG: ${contentControl.tag}. Error: ${err}`
      );
    }
  } else {
    console.warn(`Tag not found for contentControl with ID: ${contentControl.id}.`);
  }
  if (!parsedCoObject) {
    console.warn("Can not parse coObject from tag, trying to read from customXml");
    parsedCoObject = await getCustomXmlByContentControlId(contentControl.id);
  }
  return normalizeCoObject(parsedCoObject);
};

export const getCoObjectFromTagByContentControl = async (contentControl: Word.ContentControl) => {
  let parsedCoObject = undefined;
  if (contentControl?.tag) {
    try {
      parsedCoObject = JSON.parse(contentControl.tag);
    } catch (err) {
      console.warn(
        `Error For contentControl with ID: ${contentControl.id}, parsing TAG: ${contentControl.tag}. Error: ${err}`
      );
    }
  } else {
    console.warn(`Tag not found for contentControl with ID: ${contentControl.id}.`);
  }
  return normalizeCoObject(parsedCoObject);
};

export const getContentControlWithDocPartId = async (docPartId: string) => {
  const objects = await getDocumentCoObjects();
  return objects.find(
    (el) =>
      el.info.objectType === "DOCUMENT_PART_INCLUDE" &&
      (el.source as any as { path: string; includedDocumentPartId: string }).includedDocumentPartId === docPartId
  );
};

export const addOriginalPayloadToCoObject = async (item: ElaborationStatusItem) => {
  let coObject: CoObjectItem = await getCoObjectFromTagOrCustomXmlByContentControlId(Number(item.contentControlId));
  if (coObject) {
    if (isComponentInstance(coObject)) {
      console.log("Adding original payload to component instance is not supported yet");
    } else {
      coObject.originalPayload = item.originalElabResult;
      if (item.elaborationUpdateType === "SINGLE") {
        console.log("Invoke async object update");
        AsyncObjectUpdateManager.addUpdatedObject(coObject);
      }
      editTitleAndTag(coObject);
    }
  } else {
    console.error(`CoObject not found. Cannot add original payload to it. ContentControlId: ${item.contentControlId}`);
  }
};

export const getContentControlId = async (documentPartId: string): Promise<number> => {
  return Word.run(async (context) => {
    let contentControls: Word.ContentControlCollection = context.document.contentControls;
    contentControls.load(["id", "tag"]);
    await context.sync();

    console.log("Searching in tag for content control with documentPartId: ", documentPartId);
    for (const contentControl of contentControls.items) {
      try {
        const parsedCoObject = JSON.parse(contentControl.tag);
        if (parsedCoObject?.info?.objectType === CoObjectTypeEnum.DOCUMENT_PART_INCLUDE) {
          let source = parsedCoObject.source as DocumentPartSource;
          if (source.includedDocumentPartId === documentPartId) {
            return parsedCoObject.contentControlId;
          }
        }
      } catch (err) {
        console.warn(
          `Error For contentControl with ID: ${contentControl.id}, parsing TAG: ${contentControl.tag}. Skipping to next content control. Error: ${err}`
        );
      }
    }

    console.log("Searching in customXml for content control with documentPartId: ", documentPartId);
    for (const contentControl of contentControls.items) {
      const parsedCoObject = await getCustomXmlByContentControlId(contentControl.id);
      if (parsedCoObject?.info?.objectType === CoObjectTypeEnum.DOCUMENT_PART_INCLUDE) {
        let source = parsedCoObject.source as DocumentPartSource;
        if (source.includedDocumentPartId === documentPartId) {
          return parsedCoObject.contentControlId;
        }
      }
    }

    console.log("Content control with documentPartId: ", documentPartId, " not found");
    return null;
  });
};

const shouldRemoveDimensionsForNonTagetikAmount = (coObject: CoObjectItem) => {
  return (
    coObject.info.objectType === CoObjectTypeEnum.AMOUNT &&
    (coObject.source as TgkAmountObjectSource).objectSource !== SourceTypeEnum.TAGETIK &&
    coObject.source.dimensions?.length === 0
  );
};

const normalizeDimensionSupportedTypes = (coObject: CoObjectItem) => {
  if (!coObject.source) return;

  if (shouldRemoveDimensionsForNonTagetikAmount(coObject)) {
    coObject.source.dimensions = undefined;
  } else if (!coObject.source.dimensions) {
    coObject.source.dimensions = [];
  }
};

export const normalizeCoObject = (coObject: CoObjectItem) => {
  if (!coObject) return coObject;
  coObject = { ...coObject }; // shallow copy to avoid mutating the original object

  const source = coObject.source as TgkAmountObjectSource;
  if (
    source?.objectSource === SourceTypeEnum.EXCEL &&
    source.sheet?.code &&
    source.area?.name &&
    !source.cell &&
    !source.area.name.includes("!") &&
    source.sheet.code !== source.area.name
  ) {
    coObject.source = {
      ...source,
      area: { ...source.area, name: `${source.sheet.code}!${source.area.name}` },
    } as CoObjectItem["source"];
  }

  const objectType = coObject.info?.objectType as CoObjectTypeEnum;
  if (
    [
      CoObjectTypeEnum.AMOUNT,
      CoObjectTypeEnum.DIMENSION_ATTRIBUTE,
      CoObjectTypeEnum.TGK_TEXT,
      CoObjectTypeEnum.AMOUNT_AIH,
    ].includes(objectType)
  ) {
    normalizeDimensionSupportedTypes(coObject);
  } else {
    coObject.parameters = coObject.parameters ?? [];
    if (objectType === CoObjectTypeEnum.TABLE && coObject.source?.elabResultType === "HTML") {
      coObject.source.elabResultType = "STRINGBASE64";
    }
    if (coObject.source?.dimensions?.length === 0) {
      coObject.source.dimensions = undefined;
    }
  }

  return coObject;
};

function buildSummary(parsed: CoObjectItem, text: string) {
  return {
    id: parsed.id,
    documentId: parsed.documentId,
    contentControlId: parsed.contentControlId,
    objectName: parsed.info?.objectName,
    objectType: parsed.info?.objectType,
    formatMetadata: parsed.advanced?.formatMetadata,
    text,
  };
}

export const buildCoObjectSummaries = async (
  coObjectIds: Array<string | number>
): Promise<Record<string, CoObjectSummary>> => {
  if (!Array.isArray(coObjectIds) || coObjectIds.length === 0) return {};

  return Word.run(async (context) => {
    const wanted = new Map<string, { cellAddress: CellAddress; originalObjectId: string }[]>();
    for (const x of coObjectIds) {
      const id = String(x);
      const underscoreIdx = id.indexOf("_");
      if (underscoreIdx !== -1) {
        const suffix = id.slice(underscoreIdx + 1);
        const dashIdx = suffix.indexOf("-");
        const row = Number(suffix.slice(0, dashIdx));
        const column = Number(suffix.slice(dashIdx + 1));
        if (dashIdx !== -1 && !isNaN(row) && !isNaN(column)) {
          const tableId = id.slice(0, underscoreIdx);
          const cells = wanted.get(tableId) ?? [];
          cells.push({ cellAddress: { row, column }, originalObjectId: String(x) });
          wanted.set(tableId, cells);
          continue;
        }
      }
      wanted.set(id, []);
    }
    const result: Record<string, CoObjectSummary> = {};

    // sync #1 — carica items + id/tag in un unico round-trip
    const ccs = context.document.contentControls;
    ccs.load("id, tag");
    await context.sync();

    type SimpleWork = { parsed: CoObjectItem; range: Word.Range };
    type TableWork = {
      parsed: CoObjectItem;
      cells: { cellAddress: CellAddress; originalObjectId: string }[];
      table: Word.Table;
    };

    const simpleWork: SimpleWork[] = [];
    const tableWork: TableWork[] = [];

    for (const cc of ccs.items) {
      let parsed: CoObjectItem = null;
      try {
        if (cc.tag) parsed = JSON.parse(cc.tag);
      } catch {
        console.error("Retrieved malformed tag, we skip it");
      }
      if (parsed && wanted.has(parsed.id)) {
        // heal stale contentControlId: doc-part re-inclusion re-mints Word's native cc.id, so the
        // stored value in the tag JSON can be stale for nested objects; stamp the live id here so
        // it flows into buildSummary for both simple objects and cells (parsedForCell spreads ...parsed).
        parsed.contentControlId = cc.id;
        if (wanted.get(parsed.id).length > 0) {
          // table CC: accoda getFirstOrNullObject senza sync
          const table = cc.getRange().tables.getFirstOrNullObject();
          tableWork.push({ parsed, cells: wanted.get(parsed.id), table });
        } else {
          // CC semplice: carica il testo senza sync
          const range = cc.getRange(); //TODO address differences between there and ObjectListItemRowExpandable (cc.body.text)
          range.load("text");
          simpleWork.push({ parsed, range });
        }
      }
    }

    // sync #2 — risolve: testi CC semplici + isNullObject di tutte le tabelle
    await context.sync();

    for (const { parsed, range } of simpleWork) {
      const text = (range.text ?? "").toString().trim();
      result[parsed.id] = buildSummary(parsed, text);
    }

    type CellWork = {
      cell: Word.TableCell;
      cellDescriptor: { cellAddress: CellAddress; originalObjectId: string };
      parsed: CoObjectItem;
    };
    const cellWork: CellWork[] = [];

    for (const { parsed, cells, table } of tableWork) {
      if (!table.isNullObject) {
        for (const cellDescriptor of cells) {
          // accoda getCellOrNullObject senza sync
          const cell = table.getCellOrNullObject(cellDescriptor.cellAddress.row, cellDescriptor.cellAddress.column);
          cellWork.push({ cell, cellDescriptor, parsed });
        }
      }
    }

    if (cellWork.length === 0) return result;

    // sync #3 — risolve isNullObject di tutte le celle
    await context.sync();

    type RangeWork = {
      range: Word.Range;
      cellDescriptor: { cellAddress: CellAddress; originalObjectId: string };
      parsed: CoObjectItem;
    };
    const rangeWork: RangeWork[] = [];

    for (const { cell, cellDescriptor, parsed } of cellWork) {
      if (!cell.isNullObject) {
        const range = cell.body.getRange(); //TODO address differences between there and ObjectListItemRowExpandable (cell.body.text)
        range.load("text");
        rangeWork.push({ range, cellDescriptor, parsed });
      }
    }

    if (rangeWork.length === 0) return result;

    // sync #4 — risolve i testi di tutte le celle
    await context.sync();

    for (const { range, cellDescriptor, parsed } of rangeWork) {
      const text = (range.text ?? "").toString().trim();
      // copia parsed per evitare la mutazione dell'oggetto originale
      const parsedForCell: CoObjectItem = {
        ...parsed,
        id: cellDescriptor.originalObjectId,
        info: { ...parsed.info, objectType: CoObjectTypeEnum.TABLE_CELL },
      };
      result[cellDescriptor.originalObjectId] = {
        ...buildSummary(parsedForCell, text),
        row: cellDescriptor.cellAddress.row,
        column: cellDescriptor.cellAddress.column,
      };
    }

    return result;
  });
};
